import React, { useState, useEffect } from 'react';
import svg_ro from './../../assets/ro.svg';
import { parse } from 'svgson';
import './RomaniaMap.css';

// Mapare județe -> coduri NUTS3
const nutsMapping = {
  ROAB: 'RO121', // Alba
  ROAR: 'RO421', // Arad
  ROAG: 'RO311', // Argeș
  ROBC: 'RO211', // Bacău
  ROBH: 'RO111', // Bihor
  ROBN: 'RO112', // Bistrița-Năsăud
  ROBT: 'RO212', // Botoșani
  ROBR: 'RO122', // Brașov
  ROBZ: 'RO222', // Buzău
  ROCS: 'RO422', // Caraș-Severin
  ROCL: 'RO113', // Cluj
  ROCT: 'RO313', // Constanța
  ROCV: 'RO123', // Covasna
  RODB: 'RO314', // Dâmbovița
  RODJ: 'RO411', // Dolj
  ROGL: 'RO223', // Galați
  ROGR: 'RO315', // Giurgiu
  ROGJ: 'RO412', // Gorj
  ROHR: 'RO124', // Harghita
  ROHD: 'RO125', // Hunedoara
  ROIF: 'RO317', // Ilfov
  ROIS: 'RO213', // Iași
  ROMM: 'RO114', // Maramureș
  ROMH: 'RO413', // Mehedinți
  ROMS: 'RO126', // Mureș
  RONM: 'RO214', // Neamț
  ROOT: 'RO414', // Olt
  ROPH: 'RO318', // Prahova
  ROSM: 'RO115', // Satu Mare
  ROSJ: 'RO116', // Sălaj
  ROSB: 'RO127', // Sibiu
  ROSV: 'RO215', // Suceava
  ROTR: 'RO319', // Teleorman
  ROTM: 'RO423', // Timiș
  ROVS: 'RO216', // Vaslui
  ROVL: 'RO415', // Vâlcea
  ROVN: 'RO225', // Vrancea
  ROTL: 'RO224', // Tulcea (corectat)
  ROBU: 'RO321', // București
  ROCJ: 'RO316', // Călărași
  ROIL: 'RO322', // Ialomița (corectat)
};

const RomaniaMap = () => {
  const [counties, setCounties] = useState([]);
  const [selectedCounty, setSelectedCounty] = useState(null);

  useEffect(() => {
    fetch(svg_ro)
      .then((response) => response.text())
      .then((svgText) => parse(svgText))
      .then((parsedSvg) => {
        const paths = parsedSvg.children.find((child) => child.name === 'g');
        if (paths) {
          const countyPaths = paths.children
            .filter((child) => child.name === 'path')
            .map((path) => {
              const oldId = path.attributes.id || '';
              const nutsCode = nutsMapping[oldId] || oldId; // Înlocuim cu codul NUTS dacă există
              const countyName =
                path.attributes['data-name'] ||
                path.attributes['name'] ||
                'Județ Necunoscut'; // Corecție mapare nume județ

              return {
                id: nutsCode,
                name: countyName,
                d: path.attributes.d || '',
              };
            });
          setCounties(countyPaths);
        }
      })
      .catch((error) =>
        console.error('Eroare la extragerea datelor din SVG:', error),
      );
  }, []);

  const handleCountyClick = (county) => {
    setSelectedCounty(county);
  };

  return (
    <div className="space-y-6">
      {/* Harta - Full Width Sus */}
      <div className="w-full flex justify-center">
        <div className="w-full max-w-3xl">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 704"
            className="w-full h-auto"
          >
            {counties.map((county) => (
              <path
                key={county.id}
                d={county.d}
                data-name={county.name}
                onClick={() => handleCountyClick(county)}
                className={`romania-map-path ${
                  selectedCounty?.id === county.id ? 'selected' : ''
                }`}
              />
            ))}
          </svg>
        </div>
      </div>

      {/* Text și detalii - Jos */}
      <div className="space-y-4">
        {/* Card Detalii Județ Selectat - Primul */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl p-6 border-2 border-blue-400 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
              <span className="text-2xl">📍</span>
            </div>
            <h4 className="text-lg font-bold text-white">
              Detalii despre județul selectat
            </h4>
          </div>
          {selectedCounty ? (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-100">Județ:</span>
                  <span className="text-lg font-bold text-white">
                    {selectedCounty.name}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-100">Cod NUTS3:</span>
                  <span className="text-lg font-mono font-bold text-yellow-300">
                    {selectedCounty.id}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
              <p className="text-sm text-blue-100 text-center">
                👆 Selectează un județ pe hartă pentru a vedea detalii
              </p>
            </div>
          )}
        </div>

        {/* Card Info Generală - Al Doilea */}
        <div className="bg-gray-50 rounded-lg p-8 border border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            🗺️ Licitații grupate pe Județele României
          </h3>
          <p className="text-base text-gray-700 leading-relaxed">
            România este organizată administrativ în{' '}
            <strong>41 de județe</strong> și municipiul București. Fiecare
            unitate teritorială este identificată printr-un{' '}
            <strong>cod unic NUTS3</strong>, utilizat la nivel european pentru
            analiza datelor regionale și dezvoltare economică.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RomaniaMap;
